import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DuplicateReviewModal from './DuplicateReviewModal';

/**
 * DuplicateListModal - List-based duplicate review
 * 
 * Opens from the "Duplicate Submissions" card in Analytics.
 * Shows all potential duplicate groups in a scrollable list.
 * Click any group to open the existing DuplicateReviewModal.
 * 
 * NO MAP REQUIRED - direct access to duplicate management.
 */

const PBFS = {
  green: '#0DB14B',
  navy: '#00314F',
  blue: '#235EAC',
  gray: '#939598'
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function DuplicateListModal({ isOpen, onClose, backendUrl }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  
  // For opening the detail review modal
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSeed, setReviewSeed] = useState(null);
  const [reviewGroup, setReviewGroup] = useState([]);
  const [reviewReason, setReviewReason] = useState('');
  
  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState('');

  // Fetch duplicate groups on open
  useEffect(() => {
    if (isOpen) {
      fetchDuplicateGroups();
    }
  }, [isOpen]);

  const fetchDuplicateGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/duplicates/groups/potential?min_duplicates=2&max_distance_meters=100`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await res.json();
      if (data.groups) {
        setGroups(data.groups);
      } else {
        setGroups([]);
      }
    } catch (e) {
      setError(e.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter: potential vs confirmed
  const potentialGroups = useMemo(() => groups.filter(g => g.status === 'potential'), [groups]);
  const confirmedGroups = useMemo(() => groups.filter(g => g.status === 'confirmed'), [groups]);

  // Open the detailed review modal for a group
  const openGroupReview = (group) => {
    if (!group.submissions || group.submissions.length < 1) return;
    
    // Convert API format to modal format
    const seed = {
      _id: group.submissions[0].id,
      id: group.submissions[0].id,
      business_name: group.submissions[0].business_name,
      submitted_by: group.submissions[0].submitted_by,
      submitted_by_email: group.submissions[0].submitted_by_email,
      status: group.submissions[0].status,
      created_at: group.submissions[0].created_at,
      location: { lat: group.lat, lng: group.lng },
      address: group.address,
      duplicate_status: group.submissions[0].duplicate_status
    };
    
    const others = group.submissions.slice(1).map(s => ({
      _id: s.id,
      id: s.id,
      business_name: s.business_name,
      submitted_by: s.submitted_by,
      submitted_by_email: s.submitted_by_email,
      status: s.status,
      created_at: s.created_at,
      location: { lat: group.lat, lng: group.lng },
      address: group.address,
      duplicate_status: s.duplicate_status
    }));
    
    setReviewSeed(seed);
    setReviewGroup([seed, ...others]);
    setReviewReason(`${group.submission_count} submissions at ${group.location_name || 'same location'}`);
    setReviewModalOpen(true);
  };

  // Handle when duplicates are confirmed
  const handleDuplicatesConfirmed = () => {
    setReviewModalOpen(false);
    fetchDuplicateGroups(); // Refresh list
  };

  // Handle marking not duplicate
  const handleMarkedNotDuplicate = () => {
    fetchDuplicateGroups(); // Refresh list
  };

  // Open image in modal
  const openImage = (src) => {
    setImageModalSrc(src);
    setImageModalOpen(true);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[100000] flex items-center justify-center p-4 pt-20 pb-6"
      style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="bg-[var(--pbfs-navy)] border border-white/20 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="text-3xl">🔍</span>
              Duplicate Submissions Review
            </h2>
            <p className="text-white/60 text-sm mt-1">
              {groups.length} location clusters • {potentialGroups.length} need review
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl font-bold w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[var(--pbfs-green)] border-t-transparent rounded-full"></div>
              <span className="ml-3 text-white/60">Loading duplicate groups...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 text-red-200">
              <strong>Error:</strong> {error}
            </div>
          )}

          {!loading && !error && groups.length === 0 && (
            <div className="text-center py-12 text-white/60">
              <span className="text-5xl mb-4 block">✅</span>
              <p className="text-lg">No duplicate groups found!</p>
              <p className="text-sm mt-2">All submissions appear to be unique.</p>
            </div>
          )}

          {!loading && !error && potentialGroups.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-orange-400 mb-4 flex items-center gap-2">
                <span>⚠️</span> Needs Review ({potentialGroups.length})
              </h3>
              <div className="space-y-3">
                {potentialGroups.map((group, idx) => (
                  <GroupCard 
                    key={group.location_key || idx} 
                    group={group} 
                    expanded={expandedGroup === `potential-${idx}`}
                    onToggle={() => setExpandedGroup(expandedGroup === `potential-${idx}` ? null : `potential-${idx}`)}
                    onReview={() => openGroupReview(group)}
                    onImageClick={openImage}
                    backendUrl={backendUrl}
                  />
                ))}
              </div>
            </div>
          )}

          {!loading && !error && confirmedGroups.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-[var(--pbfs-green)] mb-4 flex items-center gap-2">
                <span>✅</span> Already Confirmed ({confirmedGroups.length})
              </h3>
              <div className="space-y-3">
                {confirmedGroups.map((group, idx) => (
                  <GroupCard 
                    key={group.location_key || idx} 
                    group={group} 
                    expanded={expandedGroup === `confirmed-${idx}`}
                    onToggle={() => setExpandedGroup(expandedGroup === `confirmed-${idx}` ? null : `confirmed-${idx}`)}
                    onReview={() => openGroupReview(group)}
                    onImageClick={openImage}
                    backendUrl={backendUrl}
                    confirmed
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-black/20">
          <div className="flex items-center justify-between">
            <p className="text-white/40 text-xs">
              💡 Click any group to review and confirm duplicates
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Nested: DuplicateReviewModal */}
      <DuplicateReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        seedRecord={reviewSeed}
        duplicateGroup={reviewGroup}
        reason={reviewReason}
        backendUrl={backendUrl}
        onConfirmed={handleDuplicatesConfirmed}
        onMarkedNotDuplicate={handleMarkedNotDuplicate}
      />

      {/* Image Modal */}
      {imageModalOpen && (
        <div 
          className="fixed inset-0 z-[100001] flex items-center justify-center p-4 pt-20 pb-6 bg-black/90"
          onClick={() => setImageModalOpen(false)}
        >
          <img 
            src={imageModalSrc} 
            alt="Submission" 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
          <button 
            onClick={() => setImageModalOpen(false)}
            className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-red-400 transition-colors"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}

// Individual group card
function GroupCard({ group, expanded, onToggle, onReview, onImageClick, backendUrl, confirmed }) {
  const statusColor = confirmed ? 'border-[var(--pbfs-green)]/40 bg-[var(--pbfs-green)]/10' : 'border-orange-500/40 bg-orange-500/10';
  
  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${statusColor}`}>
      {/* Header row */}
      <div 
        className="p-4 cursor-pointer hover:bg-white/5 transition-colors flex items-center justify-between"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-2xl">{confirmed ? '✅' : '⚠️'}</div>
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-semibold truncate">
              {group.location_name || 'Unknown Location'}
            </h4>
            <p className="text-white/60 text-sm truncate">
              {group.address || `${group.lat?.toFixed(4)}, ${group.lng?.toFixed(4)}`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-center">
            <div className="text-xl font-bold text-white">{group.submission_count}</div>
            <div className="text-xs text-white/40">submissions</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-white/80">{group.user_count}</div>
            <div className="text-xs text-white/40">users</div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onReview(); }}
            className="px-4 py-2 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Review
          </button>
          <span className="text-white/40 text-xl">
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-white/10 p-4 bg-black/20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {group.submissions?.map((sub, idx) => (
              <SubmissionCard 
                key={sub.id} 
                submission={sub} 
                index={idx}
                onImageClick={onImageClick}
                backendUrl={backendUrl}
              />
            ))}
          </div>
          <div className="mt-4 text-xs text-white/40">
            Users: {group.users?.join(', ') || 'Unknown'}
          </div>
        </div>
      )}
    </div>
  );
}

// Individual submission card within a group
function SubmissionCard({ submission, index, onImageClick, backendUrl }) {
  const imageUrl = submission.image_url 
    ? (submission.image_url.startsWith('http') ? submission.image_url : `${backendUrl}${submission.image_url}`)
    : null;
  
  const thumbnailUrl = submission.thumbnail_url
    ? (submission.thumbnail_url.startsWith('http') ? submission.thumbnail_url : `${backendUrl}${submission.thumbnail_url}`)
    : imageUrl;

  const statusBadge = {
    approved: 'bg-green-500/20 text-green-300 border-green-500/40',
    rejected: 'bg-red-500/20 text-red-300 border-red-500/40',
    pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
    analyzed: 'bg-blue-500/20 text-blue-300 border-blue-500/40'
  }[submission.status] || 'bg-gray-500/20 text-gray-300 border-gray-500/40';

  const dupBadge = submission.duplicate_status === 'canonical' 
    ? 'bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] border-[var(--pbfs-green)]/40'
    : submission.duplicate_status === 'confirmed_duplicate'
    ? 'bg-orange-500/20 text-orange-300 border-orange-500/40'
    : null;

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 transition-colors">
      <div className="flex gap-3">
        {/* Thumbnail */}
        <div 
          className="w-16 h-16 bg-white/10 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => imageUrl && onImageClick(imageUrl)}
        >
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="Tag" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/30 text-2xl">📷</div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">
            {submission.business_name || 'No business name'}
          </p>
          <p className="text-white/60 text-xs truncate">
            {submission.submitted_by} {submission.submitted_by_email ? `(${submission.submitted_by_email})` : ''}
          </p>
          <p className="text-white/40 text-xs">
            {formatDate(submission.created_at)}
          </p>
          <div className="flex gap-1 mt-1 flex-wrap">
            <span className={`px-2 py-0.5 text-xs rounded border ${statusBadge}`}>
              {submission.status}
            </span>
            {dupBadge && (
              <span className={`px-2 py-0.5 text-xs rounded border ${dupBadge}`}>
                {submission.duplicate_status === 'canonical' ? 'Keep' : 'Dup'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DuplicateListModal;

